import { apiPost, buildPaginationBody, API_ENDPOINTS } from "../utils/apiUtils";

/**
 * Vendor Service
 * Handles vendor request management
 */
const VendorService = {
  /**
   * Get all vendor requests with pagination and filtering
   * @param {Object} options - Filter and pagination options
   * @param {string} options.status - Request status filter
   * @param {number} options.page - Page number
   * @param {number} options.pageSize - Items per page
   * @param {string} options.searchtext - Search text
   * @returns {Promise} API response
   */
  async getAllVendorRequests({ status = "all", page = 1, pageSize = 10, searchtext = "" }) {
    const body = {
      status,
      ...buildPaginationBody({ page, pageSize, searchtext }),
    };
    return apiPost(API_ENDPOINTS.VENDORS.GET_REQUESTS, body);
  },

  /**
   * Approve vendor request
   * @param {string} vendorId - Vendor ID
   * @returns {Promise} API response
   */
  async approveVendorRequest(vendorId) {
    return apiPost(API_ENDPOINTS.VENDORS.APPROVE(vendorId), {});
  },

  /**
   * Reject vendor request
   * @param {string} vendorId - Vendor ID
   * @param {string} rejectionReason - Reason for rejection
   * @returns {Promise} API response
   */
  async rejectVendorRequest(vendorId, rejectionReason) {
    const body = {
      reason: rejectionReason,
    };
    return apiPost(API_ENDPOINTS.VENDORS.REJECT(vendorId), body);
  },

  /**
   * Add a vendor profile
   * Matches admin API: POST /api/v1/admin/vendor/add
   * @param {Object} payload - Vendor data
   */
  async addVendor(payload) {
    return apiPost(API_ENDPOINTS.VENDORS.ADD, payload);
  },

  /**
   * Edit a vendor profile
   * Matches admin API: POST /api/v1/admin/vendor/edit
   * @param {Object} payload - Vendor data including vendor_id
   */
  async editVendor(payload) {
    return apiPost(API_ENDPOINTS.VENDORS.EDIT, payload);
  },

  /**
   * Get all vendor change requests with pagination and filtering
   * @param {Object} options - Filter and pagination options
   * @param {string} options.type - Request type filter (e.g., "discount")
   * @param {number} options.page - Page number
   * @param {number} options.pageSize - Items per page
   * @returns {Promise} API response
   */
  async getAllVendorChangeRequests({ type = "discount", page = 1, pageSize = 10 }) {
    const body = {
      type,
      pagination: {
        page,
        pageSize,
      },
    };
    return apiPost(API_ENDPOINTS.VENDOR_CHANGE_REQUESTS.GET_ALL, body);
  },

  /**
   * Respond to vendor change request (approve or reject)
   * @param {string} changeRequestId - Change request ID
   * @param {string} status - Response status ("approved" or "rejected")
   * @param {string} adminResponse - Admin response message
   * @returns {Promise} API response
   */
  async respondToVendorChangeRequest(changeRequestId, status, adminResponse) {
    const body = {
      status,
      admin_response: adminResponse,
    };
    return apiPost(API_ENDPOINTS.VENDOR_CHANGE_REQUESTS.RESPOND(changeRequestId), body);
  },
};

export default VendorService; 